<template>
  <div class="segment-builder--wrapper" :class="{ 'show-mode': segmentEditMode }">
    <RfModalLoading :showDialog="isUpdating" />
    <div class="segment-builder-wrapper-content">
      <div>
        <div v-for="(filter, row) in filters" :key="row + upkey" class="segment-builder-row">
          <div class="segment-builder--item _row">
            <v-select
              id="type"
              density="compact"
              variant="outlined"
              persistentPlaceholder
              hideDetails
              :items="types"
              itemTitle="name"
              itemValue="value"
              :modelValue="type(filter)"
              class="segment-builder--type _type min-w-42"
              bgColor="#ffffff"
              placeholder="Select configuration"
              :menuProps="{ maxHeight: 410, offsetY: true }"
              :disabled="userStore.isReadOnly"
              @update:modelValue="changeType($event, row)"
            />
          </div>
          <div v-if="type(filter)" class="segment-builder--item next-selection mb-auto min-w-px">
            <RfChevronIcon class="fill-beige-50 mx-1 my-auto h-6 w-6 rotate-180" />
            <component
              :is="type(filter)"
              class="segment-builder--item next-selection _subtypes mb-auto! min-w-px"
              :model="filters[row]"
              :isPinpoint="isPinpoint"
              :disabled="userStore.isReadOnly"
              :filter="filter"
              :selected="selectedKeys"
              :isInOrGroups="orGroupsIndexOf(keyName(filter[type(filter)])) >= 0"
              :currentPromoOperator="promoOperatorProp(type(filter))"
              @updateOrGroups="updateOrGroups"
              @operator-changed="handlePromoOperatorChange"
              @update:model="v => updateFilters(v, row)"
            />
          </div>
          <div
            class="absolute top-1/2 right-1.5 inline-flex -translate-y-1/2 items-center gap-4 p-0"
          >
            <RfButtonNew
              v-if="isLastRow(row)"
              size="none"
              type="plain"
              :disabled="userStore.isDisabledWithMessage"
              @click="addRow"
            >
              <RfAddCircleIcon class="h-6 w-6 fill-blue-50!" />
            </RfButtonNew>

            <RfButtonNew
              size="none"
              type="plain"
              :disabled="userStore.isDisabledWithMessage"
              @click="removeRow(row)"
            >
              <RfCloseIcon class="fill-beige-50 h-6 w-6" />
            </RfButtonNew>
          </div>
        </div>
        <div class="segment-builder-save-row">
          <RfButtonNew :disabled="userStore.isDisabledWithMessage || !updating" @click="save">
            <RfSaveIcon class="h-6 w-6" />
            <span class="pr-4"> Save </span>
          </RfButtonNew>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, isEmpty, isEqual, union, defaultTo } from "lodash-es";
import RfUser from "@/components/RfSegments/RfUser.vue";
import RfUsageMulti from "@/components/RfSegments/RfUsageMulti.vue";
import RfCustom from "@/components/RfSegments/RfCustom.vue";
import RfDevice from "@/components/RfSegments/RfDevice.vue";
import RfLocation from "@/components/RfSegments/RfLocation.vue";
import RfPromoInteractions from "@/components/RfSegments/RfPromoInteractions.vue";
import RfIntegrations from "@/components/RfSegments/RfIntegrations.vue";
import RfModalLoading from "@/views/RfModalLoading.vue";
import {
  getUsageFilterData,
  customOrUsageFilters,
  getCustomFiltersData,
  isUserFilterDisabled,
} from "@/utils/segmentsHelpers";
import { UserFilterItems } from "@/utils/constants/SegmentsConstants";
import { getSegmentTypeDevices } from "@/utils/constants/DevicesConstants";
import { useAppsStore } from "@/pinia/appsStore";
import { computed } from "vue";
import { useSegmentStore } from "@/pinia/segmentStore";
import { useUserStore } from "@/pinia/userStore";
import RfPinpointChannels from "./RfPinpointChannels.vue";
import RfUserBucket from "./RfUserBucket.vue";
import RfButton from "../buttons/RfButton.vue";
import { useCustomFields } from "@/utils/composables/useCustomFields";
import RfSaveIcon from "../icons/RfSaveIcon.vue";
import RfButtonNew from "../buttons/RfButtonNew.vue";
import RfCloseIcon from "../icons/RfCloseIcon.vue";
import RfAddCircleIcon from "../icons/RfAddCircleIcon.vue";
import RfChevronIcon from "../icons/RfChevronIcon.vue";

const pinpointAllowedFilters = ["custom", "channels"];
const customSegmentAllowedFilters = [
  "usage",
  "user",
  "user_id",
  "user_bucket",
  "device",
  "location",
  "promos",
  "custom",
  "integrations",
];

const segmentFilters = [
  { name: "Usage", value: "usage" },
  { name: "Users", value: "user" },
  { name: "User Id", value: "user_id" },
  { name: "Bucket/block", value: "user_bucket" },
  { name: "Device", value: "device" },
  { name: "Location", value: "location" },
  { name: "Interactions", value: "promos" },
  { name: "Channel type", value: "channels" },
  { name: "Custom", value: "custom" },
  { name: "Integrations", value: "integrations", flag: "adobe_client_export" },
];
export default {
  name: "RfSegmentBuilder",
  // key needs to match value in types to load comp dynamically
  components: {
    usage: RfUsageMulti,
    user: RfUser,
    user_id: RfUserBucket,
    user_bucket: RfUserBucket,
    device: RfDevice,
    location: RfLocation,
    custom: RfCustom,
    channels: RfPinpointChannels,
    promos: RfPromoInteractions,
    integrations: RfIntegrations,
    RfModalLoading,
    RfButton,
    RfButtonNew,
    RfSaveIcon,
    RfCloseIcon,
    RfAddCircleIcon,
    RfChevronIcon,
  },
  props: ["segmentEditMode", "updating"],
  emits: ["showHideForm", "segmentSaved", "update:updating"],
  setup: () => {
    const appsStore = useAppsStore();
    return {
      app: computed(() => appsStore.app),
      segmentStore: useSegmentStore(),
      userStore: useUserStore(),
      customFields: useCustomFields(),
    };
  },
  data: () => ({
    upkey: 0,
    filters: [],
    orGroups: [],
    loading: true,
    isUpdating: false,
    panel: null,
    currentPromoOperator: "AND",
  }),
  computed: {
    selectedKeys() {
      return this.filters.flatMap(v => Object.values(v).flatMap(k => Object.keys(k)));
    },
    segment() {
      return this.segmentStore.segment;
    },
    isPinpoint() {
      return this.segment?.segment_type === "pinpoint";
    },
    types() {
      return segmentFilters.filter(el => {
        if (el.flag && !this.app?.flags[el.flag]) return false;
        return this.isPinpoint
          ? pinpointAllowedFilters.includes(el.value)
          : customSegmentAllowedFilters.includes(el.value);
      });
    },

    deviceFilters() {
      return this.segment.filter.device;
    },

    locationFilters() {
      return this.segment.filter.location;
    },

    userBucketFilters() {
      return this.segment.filter.user_bucket;
    },

    promoOperatorProp() {
      return filterType => (filterType === "promos" ? this.currentPromoOperator : undefined);
    },

    channelTypesFilter() {
      return this.segment.filter.push?.channel;
    },

    userFilters() {
      return this.segment.filter.user;
    },

    userIdFilters() {
      return this.segment.filter.user_id;
    },

    integrationsFilters() {
      return this.segment.filter.integrations;
    },

    usageFilters() {
      // Group into rows where values are exactly the same, separating those in or_groups vs not
      const allTrackers = customOrUsageFilters(
        getUsageFilterData(this.customFields.appCustomFields.value),
        this.segment.custom_filter,
      );
      return Object.entries(allTrackers)
        .reduce((rows, [label, values]) => {
          if (!values?.length) return rows;

          values.forEach(v => {
            const orGroupsIndex = this.orGroupsIndexOf(label);
            const existing = rows.find(
              row =>
                isEqual(v.values, row[0][1].values) &&
                orGroupsIndex === this.orGroupsIndexOf(row[0][0]),
            );
            if (existing) {
              existing.push([label, v]);
            } else {
              rows.push([[label, v]]);
            }
          });
          return rows;
        }, [])
        .map(row => Object.fromEntries(row));
    },

    promosFilters() {
      // Group into rows where values are exactly the same
      const isEqualWithoutSlug = (
        { promo_slug: _slugA, ...valuesA },
        { promo_slug: _slugB, ...valuesB },
      ) => isEqual(valuesA, valuesB);
      let values = [];
      let operator = "AND";
      if (Array.isArray(this.segment.custom_filter.promo_interaction)) {
        values = this.segment.custom_filter.promo_interaction[0]?.values || [];
        operator = this.segment.custom_filter.promo_interaction[0]?.options?.operator || "AND";
      } else {
        values = this.segment.custom_filter.promo_interaction?.values || [];
        operator = this.segment.custom_filter.promo_interaction?.options?.operator || "AND";
      }

      return Object.values(values || [])
        .reduce((rows, values) => {
          const existing = rows.find(row => isEqualWithoutSlug(values, row[0][1]));
          if (existing) {
            existing.push([values.promo_slug, values]);
          } else {
            rows.push([[values.promo_slug, values]]);
          }
          return rows;
        }, [])
        .map(row => ({ promos: Object.fromEntries(row), options: { operator } }));
    },
  },

  watch: {
    segment() {
      this.loading = true;
      this.prepareFilters();
    },
    filters: {
      handler(_, oldVal) {
        this.$emit("update:updating", !isEmpty(oldVal) && !this.loading);
      },
      deep: true,
    },
  },

  async mounted() {
    if (!this.customFields.appCustomFields.value.length)
      await Promise.all([
        this.customFields.customFieldsStore.getAppCustomFields({
          appId: this.app.id,
          fieldType: "custom",
        }),
        this.customFields.customFieldsStore.getAppCustomFields({
          appId: this.app.id,
          fieldType: "system",
        }),
      ]);
    this.prepareFilters();
  },

  updated() {
    this.loading = false;
  },

  methods: {
    updateFilters(filter, row) {
      const newFilters = [...this.filters];
      newFilters[row] = filter;
      this.filters = newFilters;
    },
    handlePromoOperatorChange(newOperator) {
      this.currentPromoOperator = newOperator;
    },
    prepareFilters() {
      if (!this.segment) {
        this.filters = [{}];
        return;
      }

      this.orGroups = this.segment.filter.or_groups;
      this.currentPromoOperator =
        (Array.isArray(this.segment.custom_filter.promo_interaction)
          ? this.segment.custom_filter.promo_interaction?.[0]?.options?.operator
          : this.segment.custom_filter.promo_interaction?.options?.operator) || "AND";
      this.filters = [];
      this.addFilters("usage", this.usageFilters);
      this.addFilters("user", this.userFilters);
      this.addFilters("device", this.deviceFilters);
      this.addFilters("location", this.locationFilters);
      this.addFilters(
        "custom",
        customOrUsageFilters(
          getCustomFiltersData(this.customFields.appCustomFields.value),
          this.segment.custom_filter,
        ),
      );
      this.addFilters("promos", this.promosFilters);
      this.addFilters("user_bucket", this.userBucketFilters);
      this.addFilters("user_id", this.userIdFilters);
      this.addFilters("channels", this.channelTypesFilter);
      if (this.app?.flags?.adobe_client_export) {
        this.addFilters("integrations", this.integrationsFilters);
      }

      if (!this.filters?.length) this.filters = [{}];
      this.incrKey();
    },

    validDeviceFilter(data) {
      return Object.keys(getSegmentTypeDevices(this.app?.flags?.custom_devices)).includes(data);
    },

    validUsageFilter(data) {
      if (isEmpty(data)) return false;

      // usage struct is {options: {}, values: []}
      let options = data.options || {};
      options = [
        options.trend,
        options.frequency,
        options.range_type,
        options.time_period_days,
      ].some(Boolean);

      const values = !isEmpty(data.values);

      return options && values;
    },

    validUserBucketFilter(data) {
      if (isEmpty(data)) return false;
      if (Array.isArray(data) && data.filter(el => [null, "-INF", ""].includes(el)).length)
        return false;
      return true;
    },

    validCustomFilter(data) {
      return Array.isArray(data)
        ? data.filter(el => !isEmpty((el || {}).values))
        : !isEmpty((data || {}).values);
    },

    validUserFilter(data) {
      if (!data) return false;

      const filter = UserFilterItems[data];

      // Preserve unknown/legacy persisted user filters so they round-trip
      // without being silently dropped when editing/saving a segment.
      if (!filter || !filter.field) return true;

      return !isUserFilterDisabled(filter.field, this.customFields.appSystemFields.value);
    },

    validPromoFilter(data) {
      const promo = data || {};

      const isValid = [
        promo.promo_slug,
        promo.matchTypeModel !== null, // needs to return true for true/false
        (promo.interaction_types || []).length,
      ];

      return isValid.every(Boolean);
    },

    validIntegrationsFilter(data) {
      return data.values?.length;
    },

    // parse and add filter from db to filters array
    addFilters(type, conditions) {
      if (type === "user_bucket") {
        if (!this.validUserBucketFilter(conditions?.range)) return;
        this.filters.push({
          user_bucket: { range: [...conditions.range], range_type: conditions.range_type },
        });
      } else if (type === "user_id") {
        if (isEmpty(conditions?.values)) return;
        this.filters.push({
          user_id: { values: [...conditions.values], negative_match: conditions.negative_match },
        });
      } else if (type === "channels") {
        if (isEmpty(conditions)) return;
        this.filters.push({ channels: [...conditions] });
      } else if (type === "usage") {
        for (let usage of conditions) {
          if (Object.values(usage).some(values => !this.validUsageFilter(values))) continue;
          this.filters.push({ usage });
        }
      } else if (type === "promos") {
        for (let filter of conditions) {
          if (Object.values(filter.promos).some(values => !this.validPromoFilter(values))) continue;
          this.filters.push(filter);
        }
      } else if (type === "integrations") {
        for (let [key, values] of Object.entries(conditions || {})) {
          if (!this.validIntegrationsFilter(values)) continue;
          this.filters.push({ integrations: { [key]: values } });
        }
      } else {
        Object.keys(conditions || []).forEach(criteria => {
          const values = conditions[criteria];

          if (["location", "device", "user", "promos"].includes(type) && isEmpty(values)) return;
          if (type === "device" && !this.validDeviceFilter(criteria)) return;
          if (type === "custom" && !this.validCustomFilter(values)) return;
          if (type === "user" && !this.validUserFilter(criteria)) return;

          this.filters.push({ [type]: { [criteria]: values } });
        });
      }
    },

    updateOrGroups(add, labels) {
      this.orGroups = this.orGroups.filter(group => !labels.some(label => group.includes(label)));
      if (add && labels.length > 1) this.orGroups.push(labels);
    },

    async save() {
      const segment = cloneDeep(this.segment);

      const data = {};
      let channels = [];

      this.filters.forEach(filter => {
        const type = this.keyName(filter);
        if (isEmpty(type)) return;

        const criteria = this.keyName(filter[type]);
        let values = filter[type][criteria];

        // merge existing values
        if (["location", "device"].includes(type)) {
          const currentValues = defaultTo(data[type], {})[criteria];
          values = union(defaultTo(currentValues, []), values);
          values = values.filter(Boolean);
        }

        if (!data[type]) data[type] = {};

        if (filter.location_configs && filter.location_configs[criteria]) {
          if (!data.location_configs) data.location_configs = {};
          data.location_configs[criteria] = filter.location_configs[criteria];
        }

        if (type === "channels") {
          if (isEmpty(filter[type])) return;
          return (channels = filter[type]);
        }

        if (type === "user_bucket") {
          if (!data[type]) data[type] = {};
          data[type].range_type = filter[type].range_type;
          data[type].range = filter[type].range;
        } else if (type === "user_id") {
          if (!data[type]) data[type] = {};
          data[type].negative_match = filter[type].negative_match;
          data[type].values = filter[type].values;
        } else if (type === "usage") {
          for (let label of Object.keys(filter.usage)) {
            if (!Array.isArray(data.usage[label])) {
              data.usage[label] = [];
            }
            data.usage[label].push(values);
          }
        } else if (type === "promos") {
          if (!data.promos.promo_interaction?.[0]) {
            data.promos.promo_interaction = [
              {
                values: [],
                options: { operator: filter.options?.operator || "AND" },
              },
            ];
          }
          data.promos.promo_interaction[0].values.push(...Object.values(filter.promos));
        } else {
          data[type][criteria] = values;
        }
      });

      segment.filter.device = data.device;
      segment.filter.location = data.location;
      segment.filter.user = data.user;
      segment.filter.user_id = data.user_id;
      segment.filter.user_bucket = data.user_bucket;
      segment.filter.location_configs = data.location_configs;
      segment.filter.push = { ...segment.filter?.push, channel: channels };
      segment.filter.integrations = data.integrations;
      segment.filter.or_groups = this.orGroups;
      segment.custom_filter = { ...data.usage, ...data.custom, ...data.promos };
      this.isUpdating = true;
      try {
        await this.segmentStore.update({
          appId: this.$route.params.aid,
          segId: this.$route.params.sid,
          modelSegment: segment,
        });
        this.$emit("segmentSaved");
      } catch (_e) {
        // no-op
      }
      this.isUpdating = false;
      this.$emit("update:updating", false);
    },

    type(filter) {
      return this.keyName(filter);
    },

    keyName(dict) {
      return Object.keys(dict)[0];
    },

    orGroupsIndexOf(label) {
      return this.orGroups.findIndex(group => group.includes(label));
    },

    addRow() {
      this.filters.push({});
      this.incrKey();
    },

    removeRow(row) {
      if (this.filters.length > 1) {
        this.filters.splice(row, 1);
      } else {
        this.filters = [{}];
      }

      this.incrKey();
    },

    changeType(type, row) {
      this.filters[row] = { [type]: {} };
      this.incrKey();
    },

    isLastRow(row) {
      return row + 1 === this.filters.length;
    },

    incrKey() {
      this.upkey += Math.random();
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.v-chip) {
  pointer-events: none;
}
</style>
